import { z } from 'zod';

/**
 * Onboarding — Department Code Builder form schema + helpers (PR-27, refined).
 * Mirrors PR-26's server-side validation. Client-side validation is a UX
 * nicety — PR-26's API is authoritative.
 *
 * The department code is *composed* from its parts (Geo-BU-Sub-NN) rather than
 * hand-typed — see `composeDepartmentCode`. Because the code format requires a
 * 3-letter business-unit segment AND a 3-letter sub segment, both codes are
 * exactly 3 letters and the sub is required (every one of the 86 seeded codes
 * has one).
 */

/** The three geographic scopes; the label auto-fills GeographicDescription. */
export const geographicOptions = [
  { code: 'B', label: 'Branch' },
  { code: 'N', label: 'National' },
  { code: 'R', label: 'Region' },
] as const;

export const geographicLabel = (code: string): string =>
  geographicOptions.find((o) => o.code === code)?.label ?? code;

/** Matches PR-26's `^[A-Z]-[A-Z]{3}-[A-Z]{3}-\d{2}$` (e.g. B-IST-BOT-01). */
export const departmentCodePattern = /^[A-Z]-[A-Z]{3}-[A-Z]{3}-\d{2}$/;

/** A business-unit or sub code segment: exactly three letters. */
export const codeSegmentPattern = /^[A-Za-z]{3}$/;

export const departmentSchema = z.object({
  // Composed + read-only in the UI; still regex-validated as a safety net.
  DepartmentCode: z
    .string()
    .trim()
    .regex(departmentCodePattern, 'Code must look like B-IST-BOT-01 (Geo-BU-Sub-NN, all caps)'),
  DepartmentDescription: z.string().trim().min(1, 'Description is required'),
  GeographicCode: z.enum(['B', 'N', 'R'], { message: 'Choose Branch, National or Region' }),
  GeographicDescription: z.string().trim().min(1, 'Geographic description is required'),
  BusinessUnitCode: z
    .string()
    .trim()
    .regex(codeSegmentPattern, 'Business unit code must be exactly 3 letters'),
  BusinessUnitDescription: z.string().trim().min(1, 'Business unit description is required'),
  BUSubCode: z.string().trim().regex(codeSegmentPattern, 'Sub-unit code must be exactly 3 letters'),
  BUSubDescription: z.string().trim().min(1, 'Sub-unit description is required'),
});

export type DepartmentFormData = z.infer<typeof departmentSchema>;

/** Two-digit sequence, e.g. `01`. */
export const padSequence = (n: number): string => String(Math.max(1, Math.min(99, n))).padStart(2, '0');

/**
 * Compose a department code from its parts: `Geo-BU-Sub-NN`, upper-cased.
 * Returns '' when any part is missing so the UI can show a placeholder.
 */
export function composeDepartmentCode(geo?: string, bu?: string, sub?: string, seq?: string): string {
  const g = (geo ?? '').trim().toUpperCase();
  const b = (bu ?? '').trim().toUpperCase();
  const s = (sub ?? '').trim().toUpperCase();
  const n = (seq ?? '').trim();
  if (!g || !b || !s || !n) return '';
  return `${g}-${b}-${s}-${n}`;
}

/**
 * Suggest the next free 2-digit sequence for a Geo-BU-Sub prefix, given the
 * existing department codes. Returns `01` when the prefix is unused.
 */
export function suggestNextSequence(existingCodes: string[], geo: string, bu: string, sub: string): string {
  const prefix = `${geo}-${bu}-${sub}-`.toUpperCase();
  let max = 0;
  for (const code of existingCodes) {
    if (!code.toUpperCase().startsWith(prefix)) continue;
    const n = Number.parseInt(code.slice(prefix.length), 10);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return padSequence(max + 1);
}

/**
 * Add-position-title form schema (PR-28). A title plus the department it
 * belongs to (department is required at creation — PR-26). Mirrors the
 * server-side validation in OnboardingFunctions.CreatePositionTitle.
 */
export const positionTitleSchema = z.object({
  PositionTitle: z.string().trim().min(1, 'Title is required'),
  DepartmentCode: z.string().trim().min(1, 'Choose a department'),
});

export type PositionTitleFormData = z.infer<typeof positionTitleSchema>;
